import { useState } from 'react';
import { Calendar, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { format, subDays, subMonths, startOfMonth, endOfMonth, startOfYear } from 'date-fns';

export interface DateRange {
  from: Date;
  to: Date;
  label: string;
}

const presets: { label: string; getValue: () => DateRange }[] = [
  {
    label: 'Last 7 days',
    getValue: () => ({
      from: subDays(new Date(), 7),
      to: new Date(),
      label: 'Last 7 days',
    }),
  },
  {
    label: 'Last 30 days',
    getValue: () => ({
      from: subDays(new Date(), 30),
      to: new Date(),
      label: 'Last 30 days',
    }),
  },
  {
    label: 'This month',
    getValue: () => ({
      from: startOfMonth(new Date()),
      to: new Date(),
      label: 'This month',
    }),
  },
  {
    label: 'Last month',
    getValue: () => ({
      from: startOfMonth(subMonths(new Date(), 1)),
      to: endOfMonth(subMonths(new Date(), 1)),
      label: 'Last month',
    }),
  },
  {
    label: 'Last 3 months',
    getValue: () => ({
      from: subMonths(new Date(), 3),
      to: new Date(),
      label: 'Last 3 months',
    }),
  },
  {
    label: 'Last 6 months',
    getValue: () => ({
      from: subMonths(new Date(), 6),
      to: new Date(),
      label: 'Last 6 months',
    }),
  },
  {
    label: 'Year to date',
    getValue: () => ({
      from: startOfYear(new Date()),
      to: new Date(),
      label: 'Year to date',
    }),
  },
];

interface DateRangeSelectorProps {
  value: DateRange;
  onChange: (range: DateRange) => void;
}

export function DateRangeSelector({ value, onChange }: DateRangeSelectorProps) {
  const [open, setOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" className="gap-2 rounded-2xl">
          <Calendar className="h-4 w-4" />
          <span className="hidden sm:inline">{value.label}</span>
          <span className="sm:hidden">
            {format(value.from, 'MMM d')} - {format(value.to, 'MMM d')}
          </span>
          <ChevronDown className="h-4 w-4 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-56 p-2" align="end">
        <div className="space-y-1">
          {presets.map((preset) => (
            <button
              key={preset.label}
              onClick={() => {
                onChange(preset.getValue());
                setOpen(false);
              }}
              className={`w-full text-left px-3 py-2 text-sm rounded-lg transition-colors ${
                value.label === preset.label
                  ? 'bg-primary text-primary-foreground'
                  : 'hover:bg-muted'
              }`}
            >
              {preset.label}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export function getDefaultDateRange(): DateRange {
  return {
    from: subMonths(new Date(), 6),
    to: new Date(),
    label: 'Last 6 months',
  };
}
